/**
 * Pet Profile (#553) — pure, page-local humanizers.
 *
 * The Design Philosophy forbids showing raw enum strings, ISO timestamps, or
 * internal identifiers to users (anti-pattern list). These helpers turn the
 * wire values into human copy. No I/O, no React — unit-tested in
 * __tests__/dog-profile-format.test.ts.
 */

/** Title-case a snake/space token: `free_feed` → `Free Feed`. */
function humanizeToken(raw: string): string {
  return raw
    .trim()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** Sentence-case (first word capital only): `as_needed` → `As needed`. */
function humanizeSentence(raw: string): string {
  const t = humanizeToken(raw);
  return t.length > 0 ? t.charAt(0) + t.slice(1).toLowerCase() : t;
}

const FEEDING_METHOD: Record<string, string> = {
  free_feed: 'Free feed',
  scheduled: 'Scheduled',
  measured: 'Measured portions',
};
export function humanizeFeedingMethod(v: string | null): string | null {
  if (!v) return null;
  return FEEDING_METHOD[v.trim().toLowerCase()] ?? humanizeSentence(v);
}

const FOOD_TYPE: Record<string, string> = {
  dry: 'Dry',
  wet: 'Wet',
  raw: 'Raw',
  mixed: 'Mixed',
};
export function humanizeFoodType(v: string | null): string | null {
  if (!v) return null;
  return FOOD_TYPE[v.trim().toLowerCase()] ?? humanizeSentence(v);
}

const MED_FREQUENCY: Record<string, string> = {
  daily: 'Daily',
  twice_daily: 'Twice daily',
  weekly: 'Weekly',
  as_needed: 'As needed',
};
export function humanizeFrequency(v: string | null): string | null {
  if (!v) return null;
  return MED_FREQUENCY[v.trim().toLowerCase()] ?? humanizeSentence(v);
}

/** Altered/intact display word (already derived + honest server-side). */
export function humanizeAltered(
  altered: 'intact' | 'spayed' | 'neutered' | 'altered' | null,
): string | null {
  switch (altered) {
    case 'spayed':
      return 'Spayed';
    case 'neutered':
      return 'Neutered';
    case 'altered':
      return 'Altered';
    case 'intact':
      return 'Intact';
    default:
      return null;
  }
}

export function humanizeSex(sex: 'male' | 'female' | null): string | null {
  if (sex === 'male') return 'Male';
  if (sex === 'female') return 'Female';
  return null;
}

/** Generic enum → human (incident kind/status, grooming language, mood). */
export function humanizeEnum(v: string | null): string | null {
  if (!v) return null;
  return humanizeSentence(v);
}

/**
 * Behavior-note severity (1–5) → a human label + the semantic status level
 * used for its color. 1–2 read informational (neutral), 3 is a warning, 4–5
 * are danger. Text label always accompanies color (never color-only).
 */
export type SeverityLevel = 'info' | 'warning' | 'danger';
export function severityLabel(severity: number): string {
  switch (severity) {
    case 5:
      return 'Critical';
    case 4:
      return 'Significant';
    case 3:
      return 'Moderate';
    case 2:
      return 'Mild';
    default:
      return 'Info';
  }
}
export function severityLevel(severity: number): SeverityLevel {
  if (severity >= 4) return 'danger';
  if (severity === 3) return 'warning';
  return 'info';
}

/** Behavior category ('social' | 'anxiety' | …) → Title Case. */
export function humanizeCategory(v: string | null): string | null {
  if (!v) return null;
  return humanizeToken(v);
}

/**
 * Format an ISO date/datetime as `Mar 4, 2024`, computed in UTC so a
 * `@db.Date` stored as midnight-UTC never drifts a day in a western tz.
 * Returns null for null/invalid input (caller renders nothing).
 */
export function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

/** `Mar 2024` — for member-since / provenance lines. */
export function formatMonthYear(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

/**
 * Age in whole years/months from an ISO birthDate — null when not derivable
 * (null date, unparseable, or a future date). Never guesses (honesty #546).
 * Mirrors the ID-card `deriveAge` so the two surfaces agree.
 */
export function deriveAge(
  birthDate: string | null,
  now: Date = new Date(),
): string | null {
  if (!birthDate) return null;
  const born = new Date(birthDate);
  if (Number.isNaN(born.getTime())) return null;
  let months =
    (now.getUTCFullYear() - born.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - born.getUTCMonth());
  if (now.getUTCDate() < born.getUTCDate()) months -= 1;
  if (months < 0) return null;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (years === 0 && rem === 0) return 'Under 1 mo';
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} yr${years === 1 ? '' : 's'}`);
  if (rem > 0) parts.push(`${rem} mo`);
  return parts.join(' ');
}

/** Whole-dollar money → `$5,000` (tabular where rendered). */
export function formatWholeDollars(amount: number | null): string | null {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return null;
  }
  return `$${amount.toLocaleString('en-US')}`;
}

/**
 * Per-shot vax level → text mark and label (never color-only).
 *
 * Ted #32a (E1.1, #1156) — a vaccination row as the desk should READ it. A
 * row that a later record of the same vaccine replaced is history: it shows
 * as "Replaced" in neutral, never as a red "Expired" beside the renewal that
 * actually decides the dog's status. Current rows keep their per-shot level.
 */
export type VaxRowDisplay = 'expired' | 'expiring' | 'ok' | 'unknown' | 'superseded';

export function vaxRowDisplay(v: { status: string; superseded?: boolean }): VaxRowDisplay {
  if (v.superseded === true) return 'superseded';
  return v.status === 'expired' || v.status === 'expiring' || v.status === 'ok'
    ? v.status
    : 'unknown';
}

export const VAX_ROW_MARK: Record<VaxRowDisplay, string> = {
  expired: '✕',
  expiring: '!',
  ok: '✓',
  unknown: '–',
  superseded: '–',
};
export const VAX_ROW_LABEL: Record<VaxRowDisplay, string> = {
  expired: 'Expired',
  expiring: 'Expiring soon',
  ok: 'Current',
  unknown: 'No expiry on file',
  superseded: 'Replaced by a newer record',
};

/** Current records first, replaced ones after; otherwise the server's order. */
export function currentRowsFirst<T extends { superseded?: boolean }>(rows: ReadonlyArray<T>): T[] {
  return [...rows.filter((r) => r.superseded !== true), ...rows.filter((r) => r.superseded === true)];
}

/**
 * Ted #32a (#1156) — a stored vaccine key as the desk reads it:
 * "rabies" → "Rabies", "canine_influenza" → "Canine influenza", and the
 * combination vaccines' acronyms in capitals ("dhpp" → "DHPP"). Gingr's keys
 * are lower_snake (the importer's transform); this keeps an identifier from
 * reaching the screen raw.
 */
export function vaccineLabel(name: string): string {
  const spaced = name.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  if (/^(dhpp|dhlpp|dapp|da2pp|dhppi|dhlppc)$/i.test(spaced)) return spaced.toUpperCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
